import React, { SyntheticEvent, useCallback, useState, useEffect } from 'react';
import cl from 'classnames';
import some from 'lodash/some';

import type { CompanyTabNanoID } from '../../../companyTabsTypes';
import { TeamID, TeamNanoID } from '../../../../teams/teamsTypes';

import {
  FETCH_COMPANY_TABS_QUERY,
  FetchCompanyTabsQueryResponse
} from '../../../queries/fetchCompanyTabs.query';

import { useCompanyTabs } from '../../../hooks/useCompanyTabs';

import { CompanyTabsMenuDropdownItem } from '../CompanyTabsMenuDropdownItem';
import { CompanyTabsMenuDropdownCreateItem } from '../CompanyTabsMenuDropdownCreateItem';

import { ButtonHelper } from '../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../helpers/Icon';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';
import { Translate } from '../../../../../helpers/Translate';

import { CompanyTabsCache } from '../../../CompanyTabsCache';
import { words } from '../../../../../locales/keys';

import { IconsEnum } from '../../../../../assets/icons/types';

interface CompanyTabsMenuDropdownProps {
  activeTabNanoId?: CompanyTabNanoID;
  companyId: TeamID;
  companyNanoId: TeamNanoID;
  onDeleteActiveTab?: () => void;
  isMenuOpen?: boolean;
  withCreateButton: boolean;
  withUpdateButton: boolean;
  withDeleteButton: boolean;
}

function CompanyTabsMenuDropdown({
  activeTabNanoId,
  companyId,
  companyNanoId,
  onDeleteActiveTab,
  isMenuOpen = false,
  withCreateButton,
  withUpdateButton,
  withDeleteButton
}: CompanyTabsMenuDropdownProps) {
  const { companyTabs, companyTabsFetched } =
    useCompanyTabs<FetchCompanyTabsQueryResponse>({
      cacheKey: CompanyTabsCache.indexCacheKey(),
      query: FETCH_COMPANY_TABS_QUERY,
      initialFilters: { companyId: { eq: companyId } }
    });

  const [isOpen, setIsOpen] = useState<boolean>(isMenuOpen);

  useEffect(() => {
    if (!isMenuOpen) {
      setIsOpen(some(companyTabs, ['nanoId', activeTabNanoId]));
    }
  }, [activeTabNanoId, isMenuOpen, companyTabs]);

  const handleClick = useCallback<(e: SyntheticEvent) => void>(
    (e: SyntheticEvent) => {
      e.preventDefault();
      setIsOpen((prevVal) => !prevVal);
    },
    [setIsOpen]
  );

  return (
    <div className="-mx-4">
      <div className="flex items-center group relative px-4 py-2 transition-colors ease-in-out duration-150 focus-within:ring-2 focus-within:ring-gray-850 dark:focus-within:ring-gray-100 bg-gray-500 bg-opacity-0 hover:bg-opacity-20">
        <div className="pl-2 pr-1">
          <Icon
            icon={IconsEnum.VIEW_COLUMNS}
            className="h-5 w-5 flex-shrink-0"
          />
        </div>
        <div className="absolute left-1 inset-y-0 flex items-center">
          <Icon
            icon={IconsEnum.CHEVRON_RIGHT}
            className={cl(
              'text-current h-5 w-5 transform flex-shrink-0',
              isOpen ? 'rotate-90' : null
            )}
          />
        </div>
        <ButtonHelper
          className="flex-1 px-2 text-sm text-left focus:ring-0 focus:ring-offset-0"
          onClick={handleClick}
        >
          <Translate id={words.companyCustomTabs} />
          <span className="absolute inset-0" aria-hidden="true" />
        </ButtonHelper>
      </div>

      {isOpen ? (
        <LoadingSkeleton count={2} loaded={companyTabsFetched}>
          <ul className="mt-2 mb-4 px-4">
            {companyTabs?.map(({ name, nanoId, uuid }) => (
              <CompanyTabsMenuDropdownItem
                active={activeTabNanoId === nanoId}
                name={name}
                key={nanoId}
                onDeleteActiveTab={onDeleteActiveTab}
                companyTabNanoId={nanoId}
                companyTabUuid={uuid}
                companyNanoId={companyNanoId}
                withUpdateButton={withUpdateButton}
                withDeleteButton={withDeleteButton}
              />
            ))}

            {withCreateButton && (
              <CompanyTabsMenuDropdownCreateItem companyId={companyId} />
            )}
          </ul>
        </LoadingSkeleton>
      ) : null}
    </div>
  );
}

export default CompanyTabsMenuDropdown;
